// src/hooks/useLeagueData.js

import { useState, useCallback, useRef } from 'react';
import unifiedApi from '../utils/unifiedApi';

/**
 * Custom hook for managing league data fetching (both public and private)
 * @param {Object} options - Configuration options
 * @param {boolean} options.isPublic - Whether to use public API endpoints
 * @param {boolean} options.includeTeamCounts - Whether to fetch team counts for each league
 * @param {boolean} options.includeGameCounts - Whether to fetch game counts for each league
 * @returns {Object} League data and loading state
 */
export const useLeagueData = ({ 
  isPublic = false, 
  includeTeamCounts = true, 
  includeGameCounts = true 
} = {}) => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const fetchTeamCount = useCallback(async (league) => {
    if (!includeTeamCounts) return 0;
    
    try {
      if (isPublic) {
        // For public API, team count comes from the organization endpoint
        return league.teamCount || 0;
      } else {
        // For private API, fetch teams for the league
        const teams = await unifiedApi.getLeagueTeamsWithManagers(league.id);
        return Array.isArray(teams) ? teams.length : 0;
      }
    } catch (error) {
      console.warn(`Failed to load teams for league ${league.id}:`, error);
    }
    return 0;
  }, [isPublic, includeTeamCounts]);

  const fetchGameCount = useCallback(async (league) => {
    if (!includeGameCounts) return 0;
    
    try {
      if (isPublic) {
        // For public API, game count comes from the organization endpoint
        return league.gameCount || 0;
      } else {
        // For private API, fetch games for the league
        const games = await unifiedApi.getLeagueGames(league.id);
        return Array.isArray(games) ? games.length : 0;
      }
    } catch (error) {
      console.warn(`Failed to load games for league ${league.id}:`, error);
    }
    return 0;
  }, [isPublic, includeGameCounts]);

  const loadLeagues = useCallback(async () => {
    if (loadingRef.current) return; // Prevent multiple simultaneous calls
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      let rawLeagues = [];
      
      if (isPublic) {
        // Use public API
        const organization = await unifiedApi.getOrganization();
        rawLeagues = organization?.leagues || [];
      } else {
        // Use private API
        rawLeagues = (await unifiedApi.getLeagues()) || [];
      }
      
      // Enrich leagues with counts if needed
      const leaguesWithCounts = await Promise.all(
        rawLeagues.map(async (league) => {
          const [teamCount, gameCount] = await Promise.all([
            fetchTeamCount(league),
            fetchGameCount(league)
          ]);
          
          return {
            ...league,
            teamCount,
            gameCount
          };
        })
      );
      
      setLeagues(leaguesWithCounts);
    } catch (err) {
      console.error('Error loading leagues:', err);
      setError(err.message || 'Failed to load leagues');
      setLeagues([]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [isPublic, fetchTeamCount, fetchGameCount]);

  const refreshLeagues = useCallback(() => {
    return loadLeagues();
  }, [loadLeagues]);

  const deleteLeague = useCallback(async (leagueId) => {
    if (isPublic) {
      throw new Error('Cannot delete leagues in public mode');
    }
    
    try {
      await unifiedApi.deleteLeague(leagueId);
      
      // Refresh leagues after successful deletion
      await refreshLeagues();
      return true;
    } catch (err) {
      console.error('Error deleting league:', err);
      setError(err.message || 'Failed to delete league');
      return false;
    }
  }, [isPublic, refreshLeagues]);

  return {
    leagues,
    loading,
    error,
    loadLeagues,
    refreshLeagues,
    deleteLeague
  };
};